import { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { captureError } from "@/lib/errorReporting";
import { useAuth } from "@/providers";
import { usePostHog } from "posthog-js/react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useConnection } from "@solana/wallet-adapter-react";
import {
    SystemProgram,
    PublicKey,
    Transaction,
    LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import { TopBar, SkeletonCard, Icon, useToast } from "@/components/ui";
import Button from "@/components/ui/Button";
import {
    estimateTokensForDeposit,
    formatTokenAmount,
    getCurveParams,
} from "@/utils/curveEstimator";
import { useConfig } from "@/providers";
import { isMobileBrowser } from "@/utils/mobile";
import MobileWalletSheet from "@/components/MobileWalletSheet";
import { useRealtimeCabal } from "@/hooks/useRealtimeCabal";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ─── Types ─── */
interface CabalDetail {
    _id: string;
    name: string;
    ticker: string;
    slug: string;
    lore?: string;
    imageUrl?: string;
    status: "recruiting" | "launched" | "failed";
    depositTarget: number;
    depositCurrent: number;
    targetMcap: number;
    leaderWallet: string;
    leaderDeposit: number;
    memberCount: number;
    inviteCode: string;
    minDeposit: number;
    maxDeposit: number;
    curveEstimate?: {
        sqrtPrice: number;
        totalSolCommitted: number;
        totalTokensEstimated: number;
    };
}

interface PendingDeposit {
    cabalSlug: string;
    managedPublicKey: string;
    amount: number;
    txSignature?: string;
    timestamp: number;
    step: "prepared" | "signed";
}

const PENDING_DEPOSIT_KEY = "codev_pending_deposit";
const PENDING_DEPOSIT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const QUICK_AMOUNTS = [1, 2, 5];

/* ════════════════════════════════════════════
   Deposit / Stake Capital Page
   ════════════════════════════════════════════ */
export default function DepositCabalPage(): ReactNode {
    const { cabalId } = useParams<{ cabalId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useAuth();
    const { publicKey, sendTransaction, wallet, wallets, select, connect } = useWallet();
    const { connection } = useConnection();
    const { TOTAL_TOKEN_SUPPLY, FEE_RESERVE_SOL, INITIAL_MC_SOL, MIGRATION_MC_SOL } = useConfig();

    const posthog = usePostHog();
    const { toast } = useToast();
    const isLeader = (location.state as { isLeader?: boolean })?.isLeader ?? false;

    const [cabal, setCabal] = useState<CabalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState("");
    const rawAmountRef = useRef<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [depositStep, setDepositStep] = useState<"idle" | "preparing" | "signing" | "confirming">("idle");
    const [error, setError] = useState<string | null>(null);
    const [phantomBalance, setPhantomBalance] = useState<number>(0);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [remainingCapacity, setRemainingCapacity] = useState<number | null>(null);
    const [pendingRecovery, setPendingRecovery] = useState<PendingDeposit | null>(null);
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const submittingRef = useRef(false);

    const connectWallet = useCallback(async () => {
        // On mobile, always show mobile wallet picker (deep links + browser wallets)
        if (isMobileBrowser()) {
            setMobileSheetOpen(true);
            return;
        }
        if (!wallet) {
            const phantom = wallets.find((w) =>
                w.adapter.name.toLowerCase().includes("phantom")
            );
            if (phantom) {
                select(phantom.adapter.name);
                setTimeout(() => connect().catch((err) => captureError("Wallet connect error:", err)), 100);
                return;
            }
        }
        connect().catch((err) => captureError("Wallet connect error:", err));
    }, [wallet, wallets, select, connect]);

    // Fetch cabal details
    useEffect(() => {
        if (!cabalId) return;
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/cabals/${cabalId}`, { signal: controller.signal })
            .then((r) => {
                if (!r.ok) throw new Error("Not found");
                return r.json();
            })
            .then((data) => setCabal(data))
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [cabalId]);

    // Fetch Phantom SOL balance
    useEffect(() => {
        async function fetchBalance() {
            if (!publicKey || !connection) return;
            setBalanceLoading(true);
            try {
                const lamports = await connection.getBalance(publicKey);
                setPhantomBalance(lamports / LAMPORTS_PER_SOL);
            } catch (err) {
                captureError("Failed to fetch Phantom balance:", err);
            } finally {
                setBalanceLoading(false);
            }
        }
        fetchBalance();
    }, [publicKey, connection]);

    // ─── beforeunload warning during active deposit (Fix 5.7) ───
    useEffect(() => {
        if (!submitting) return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [submitting]);

    // ─── Recover pending deposit from localStorage (Fix 5.1) ───
    useEffect(() => {
        const raw = localStorage.getItem(PENDING_DEPOSIT_KEY);
        if (!raw) return;
        try {
            const pending: PendingDeposit = JSON.parse(raw);
            if (Date.now() - pending.timestamp > PENDING_DEPOSIT_TTL_MS) {
                localStorage.removeItem(PENDING_DEPOSIT_KEY);
                return;
            }
            if (pending.cabalSlug !== cabalId) return;
            if (pending.txSignature && pending.step === "signed") {
                setPendingRecovery(pending);
            }
        } catch {
            localStorage.removeItem(PENDING_DEPOSIT_KEY);
        }
    }, [cabalId]);

    // ─── Real-time deposit progress from Firebase RTDB ───
    const realtime = useRealtimeCabal(cabal?.slug);
    const liveDepositCurrent = realtime.depositProgress?.current ?? cabal?.depositCurrent ?? 0;
    const liveDepositTarget = realtime.depositProgress?.target ?? cabal?.depositTarget ?? 0;
    // liveMemberCount can be derived from: realtime.members?.count ?? cabal?.memberCount ?? 0

    const numAmount = rawAmountRef.current ?? (parseFloat(amount) || 0);
    // ─── Bonding curve token estimate ───
    const curveParams = useMemo(() => getCurveParams(INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY), [INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY]);
    const currentSqrtPrice = cabal?.curveEstimate?.sqrtPrice || curveParams.sqrtStartPrice;

    const { estAllocation, supplyPct } = useMemo(() => {
        if (!cabal || numAmount <= 0) return { estAllocation: "0", supplyPct: "0.00" };
        const { tokensOut } = estimateTokensForDeposit(
            numAmount,
            currentSqrtPrice,
            curveParams,
            FEE_RESERVE_SOL,
        );
        const pct = (tokensOut / TOTAL_TOKEN_SUPPLY) * 100;
        return {
            estAllocation: formatTokenAmount(tokensOut),
            supplyPct: pct < 0.01 ? pct.toFixed(4) : pct.toFixed(2),
        };
    }, [cabal, numAmount, currentSqrtPrice, curveParams, TOTAL_TOKEN_SUPPLY, FEE_RESERVE_SOL]);

    // Effective max: lesser of per-user max and remaining cabal capacity
    // const effectiveMax = cabal
    //     ? Math.min(
    //         cabal.maxDeposit,
    //         remainingCapacity ?? (cabal.depositTarget - cabal.depositCurrent)
    //     )
    //     : 0;

    const rawRemaining = cabal
        ? (remainingCapacity ?? (liveDepositTarget - liveDepositCurrent))
        : 0;

    // Rule 2: remaining < FEE_RESERVE_SOL means no viable deposit — treat as full
    const depositsFull = rawRemaining <= FEE_RESERVE_SOL;

    const effectiveMax = cabal
        ? (depositsFull ? 0 : Math.min(5, rawRemaining))
        : 0;
    const effectiveMaxDisplay = parseFloat(effectiveMax.toFixed(4));

    // Rule 1: Lower the min-deposit floor when remaining capacity is less than
    // the normal minimum but can still cover on-chain fee reserve.
    const effectiveMin = cabal
        ? (depositsFull ? 0
            : rawRemaining < cabal.minDeposit ? FEE_RESERVE_SOL
                : cabal.minDeposit)
        : 0;

    const isValidAmount = cabal
        ? numAmount >= effectiveMin && numAmount <= effectiveMax
        : false;

    const hasEnoughSOL = publicKey && phantomBalance >= numAmount + 0.01; // buffer for tx fees

    /**
     * New deposit flow (Phantom direct transfer):
     * 1. POST /api/cabals/:cabalId/prepare-deposit → get managed wallet pubkey
     * 2. Build Phantom tx: transfer SOL from Phantom → managed wallet
     * 3. POST /api/cabals/:cabalId/deposit { amount, txSignature } → confirm
     */
    async function handleDeposit() {
        if (submittingRef.current) return; // synchronous double-click guard (Fix 5.2)
        if (!cabal || !isValidAmount || !token || !publicKey || !sendTransaction) return;

        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        let lastStep: "prepared" | "signed" | null = null;
        posthog.capture("deposit_started", {
            cabalSlug: cabal.slug,
            ticker: cabal.ticker,
            amount: numAmount,
            isLeader,
        });

        try {
            // ─── Step 1: Prepare deposit — get/create managed wallet ───
            setDepositStep("preparing");
            const prepRes = await fetch(`${API_BASE}/api/cabals/${cabal.slug}/prepare-deposit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!prepRes.ok) {
                const errData = await prepRes.json().catch(() => ({}));
                throw new Error(errData.error || "Failed to prepare deposit");
            }

            const { managedPublicKey, remainingCapacity: capacity } = await prepRes.json();
            setRemainingCapacity(capacity);

            // Persist deposit state after prepare (Fix 5.1)
            lastStep = "prepared";
            localStorage.setItem(PENDING_DEPOSIT_KEY, JSON.stringify({
                cabalSlug: cabal.slug, managedPublicKey, amount: numAmount,
                timestamp: Date.now(), step: "prepared",
            }));

            // ─── Step 2: Build and send Phantom transaction ───
            setDepositStep("signing");
            const lamports = Math.round(numAmount * LAMPORTS_PER_SOL);
            const transaction = new Transaction().add(
                SystemProgram.transfer({
                    fromPubkey: publicKey,
                    toPubkey: new PublicKey(managedPublicKey),
                    lamports,
                })
            );

            const txSignature = await sendTransaction(transaction, connection);

            // Persist with txSignature — SOL has been sent (Fix 5.1)
            lastStep = "signed";
            localStorage.setItem(PENDING_DEPOSIT_KEY, JSON.stringify({
                cabalSlug: cabal.slug, managedPublicKey, amount: numAmount,
                txSignature, timestamp: Date.now(), step: "signed",
            }));

            // Wait for confirmation
            setDepositStep("confirming");
            await connection.confirmTransaction(txSignature, "confirmed");

            // ─── Step 3: Confirm deposit with backend ───
            const confirmRes = await fetch(`${API_BASE}/api/cabals/${cabal.slug}/deposit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ amount: numAmount, txSignature }),
            });

            if (!confirmRes.ok) {
                const errData = await confirmRes.json().catch(() => ({}));

                // Sender mismatch — backend already refunded SOL to the actual sender
                if (errData.senderMismatch) {
                    toast(
                        "Transaction sender does not match your connected wallet. SOL will be refunded.",
                        "error",
                        6000
                    );
                    if (errData.refundTxSignature) {
                        const shortSig = errData.refundTxSignature.slice(0, 8) + "...";
                        const cluster = (import.meta.env.VITE_SOLANA_NETWORK || "devnet") === "mainnet-beta" ? "" : "?cluster=devnet";
                        toast(
                            `SOL refunded (${shortSig}). View on solscan.io/tx/${errData.refundTxSignature}${cluster}`,
                            "success",
                            8000
                        );
                    }
                    localStorage.removeItem(PENDING_DEPOSIT_KEY);
                    setPendingRecovery(null);
                    return;
                }

                throw new Error(errData.error || "Failed to confirm deposit");
            }

            const data = await confirmRes.json();

            // Deposit confirmed — clear pending state (Fix 5.1)
            localStorage.removeItem(PENDING_DEPOSIT_KEY);
            setPendingRecovery(null);

            // Show toast if backend reduced the amount (Fix 5.4)
            if (data.wasClamped) {
                toast(
                    `Deposit clamped to ${data.acceptedAmount} SOL (requested ${numAmount} SOL)`,
                    "warning",
                    5000
                );
            }

            // Show toast when this deposit activated the codev (draft → recruiting)
            if (data.cabalActivated) {
                toast(
                    `New codev launched: ${data.ticker} — ${data.name}`,
                    "info",
                    5000
                );
            }

            const actualAmount = data.acceptedAmount ?? numAmount;

            // Refresh Phantom balance
            try {
                const lamports = await connection.getBalance(publicKey);
                setPhantomBalance(lamports / LAMPORTS_PER_SOL);
            } catch { }

            posthog.capture("deposit_success", {
                cabalSlug: cabal.slug,
                ticker: cabal.ticker,
                amount: actualAmount,
                isLeader,
                wasClamped: data.wasClamped,
            });

            // Navigate to success page
            if (isLeader) {
                const updatedCabal = {
                    ...cabal,
                    depositCurrent: data.cabalProgress ?? cabal.depositCurrent + actualAmount,
                    leaderDeposit: actualAmount,
                };
                navigate(`/create/success`, {
                    state: { cabal: updatedCabal },
                });
            } else {
                navigate(`/codevs/${cabal.slug}/staked`, {
                    state: {
                        cabal: {
                            ...cabal,
                            depositCurrent: data.cabalProgress ?? cabal.depositCurrent + actualAmount,
                        },
                        deposit: actualAmount,
                        stakeWeight: data.stakeWeight,
                        totalDeposited: data.totalDeposited,
                        txSignature: data.txSignature,
                        solscanUrl: data.solscanUrl,
                        wasClamped: data.wasClamped,
                        requestedAmount: numAmount,
                        estimatedTokens: data.estimatedTokens,
                    },
                });
            }
        } catch (err) {
            captureError("Deposit error:", err, {
                cabalId: cabal?.slug || "",
            });
            posthog.capture("deposit_failed", {
                cabalSlug: cabal?.slug,
                ticker: cabal?.ticker,
                amount: numAmount,
                error: err instanceof Error ? err.message : "Unknown error",
            });
            const errMsg = err instanceof Error ? err.message : "Deposit failed";
            setError(errMsg);
            toast(errMsg, "error");
            // Only clear localStorage if SOL was never sent (Fix 5.1)
            if (lastStep !== "signed") {
                localStorage.removeItem(PENDING_DEPOSIT_KEY);
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
            setDepositStep("idle");
        }
    }

    /** Retry step 3 for a recovered pending deposit (Fix 5.1) */
    async function handleRecovery() {
        if (!pendingRecovery || !token || !cabal || submittingRef.current) return;

        submittingRef.current = true;
        setSubmitting(true);
        setError(null);

        try {
            const confirmRes = await fetch(`${API_BASE}/api/cabals/${cabal.slug}/deposit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    amount: pendingRecovery.amount,
                    txSignature: pendingRecovery.txSignature,
                }),
            });

            if (!confirmRes.ok) {
                const errData = await confirmRes.json().catch(() => ({}));

                // Sender mismatch — backend already refunded SOL to the actual sender
                if (errData.senderMismatch) {
                    toast(
                        "Transaction sender does not match your connected wallet. SOL will be refunded.",
                        "error",
                        6000
                    );
                    if (errData.refundTxSignature) {
                        const shortSig = errData.refundTxSignature.slice(0, 8) + "...";
                        const cluster = (import.meta.env.VITE_SOLANA_NETWORK || "devnet") === "mainnet-beta" ? "" : "?cluster=devnet";
                        toast(
                            `SOL refunded (${shortSig}). View on solscan.io/tx/${errData.refundTxSignature}${cluster}`,
                            "success",
                            8000
                        );
                    }
                    localStorage.removeItem(PENDING_DEPOSIT_KEY);
                    setPendingRecovery(null);
                    return;
                }

                throw new Error(errData.error || "Failed to confirm deposit");
            }

            const data = await confirmRes.json();
            localStorage.removeItem(PENDING_DEPOSIT_KEY);
            setPendingRecovery(null);

            if (data.wasClamped) {
                toast(
                    `Deposit clamped to ${data.acceptedAmount} SOL (requested ${pendingRecovery.amount} SOL)`,
                    "warning",
                    5000
                );
            }

            const actualAmount = data.acceptedAmount ?? pendingRecovery.amount;
            toast(`Recovered deposit of ${actualAmount} SOL`, "success");

            posthog.capture("deposit_success", {
                cabalSlug: cabal.slug,
                ticker: cabal.ticker,
                amount: actualAmount,
                isLeader,
                recovered: true,
            });

            if (isLeader) {
                navigate(`/create/success`, {
                    state: { cabal: { ...cabal, depositCurrent: data.cabalProgress ?? cabal.depositCurrent + actualAmount, leaderDeposit: actualAmount } },
                });
            } else {
                navigate(`/codevs/${cabal.slug}/staked`, {
                    state: {
                        cabal: { ...cabal, depositCurrent: data.cabalProgress ?? cabal.depositCurrent + actualAmount },
                        deposit: actualAmount,
                        stakeWeight: data.stakeWeight,
                        totalDeposited: data.totalDeposited,
                        txSignature: data.txSignature,
                        solscanUrl: data.solscanUrl,
                        wasClamped: data.wasClamped,
                        requestedAmount: pendingRecovery.amount,
                        estimatedTokens: data.estimatedTokens,
                    },
                });
            }
        } catch (err) {
            captureError("Deposit recovery error:", err, { cabalId: cabal?.slug || "" });
            setError(err instanceof Error ? err.message : "Recovery failed");
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }

    const stepLabel =
        depositStep === "preparing"
            ? "Preparing Wallet..."
            : depositStep === "signing"
                ? "Sign in Wallet..."
                : depositStep === "confirming"
                    ? "Confirming On-Chain..."
                    : submitting
                        ? "Processing..."
                        : "Confirm Deposit";


    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            <TopBar
                variant="minimal"
                showBack
                onBack={() => navigate(-1)}
                title="Join Launch Pool"
            />

            <main className="relative z-10 flex-1 flex flex-col gap-[clamp(12px,1.7dvh,16px)] px-[clamp(16px,2.6dvh,24px)] pb-[clamp(80px,10.7dvh,112px)]">
                {loading ? (
                    <div className="space-y-4">
                        <div className="h-20 rounded-[12px] bg-white/5 animate-pulse" />
                        <SkeletonCard count={2} />
                    </div>
                ) : !cabal ? (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-white/40 text-[14px] font-medium">Cabal not found</p>
                    </div>
                ) : (
                    <>
                        {/* ─── Pending Deposit Recovery Banner (Fix 5.1) ─── */}
                        {pendingRecovery && (
                            <button
                                onClick={handleRecovery}
                                disabled={submitting}
                                className="w-full bg-amber-500/10 border border-amber-500/30 rounded-[12px] p-3 flex items-center gap-3 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                            >
                                <div className="text-left flex-1">
                                    <p className="text-[11px] font-bold text-amber-400 leading-tight">
                                        Previous deposit of {pendingRecovery.amount} SOL may not have been recorded.
                                    </p>
                                    <p className="text-[9px] text-amber-400/60 mt-0.5">
                                        Tap to retry confirmation
                                    </p>
                                </div>
                            </button>
                        )}

                        {/* ─── Coin Header ─── */}
                        <div className="flex items-center justify-center gap-[8px]">
                            <div className="flex items-center justify-center p-[3.375px] rounded-full size-[clamp(58px,7.7dvh,72px)]">
                                <div className="w-full h-full rounded-full overflow-hidden border-[2px] border-white/20">
                                    {cabal.imageUrl ? (
                                        <img
                                            alt={cabal.ticker}
                                            className="w-full h-full object-contain"
                                            src={cabal.imageUrl}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                                            <span className="text-[26px] font-bold text-white">
                                                {cabal.ticker.toUpperCase().charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <p className="font-medium leading-[1.13] text-[clamp(20px,2.8dvh,26px)] text-white tracking-[-0.52px] truncate max-w-[200px]">
                                {cabal.ticker.toUpperCase()}
                            </p>
                        </div>

                        {/* ─── Codev Progress ─── */}
                        <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border border-[rgba(153,153,153,0.5)] rounded-[12px] overflow-clip p-[16px] flex flex-col gap-[24px]">
                            <div className="flex items-start justify-between font-semibold">
                                <p className="text-[14px] text-white leading-[1.15]">
                                    Launch Pool Progress
                                </p>
                                <div className="flex gap-[8px] items-start text-[16px]">
                                    <p className="text-[rgba(255,255,255,0.5)]">
                                        {parseFloat(liveDepositCurrent.toFixed(2))}/{parseFloat(liveDepositTarget.toFixed(2))} SOL
                                    </p>
                                    <p className="text-[#f3ce28]">
                                        {liveDepositTarget > 0
                                            ? Math.min(Math.round((liveDepositCurrent / liveDepositTarget) * 100), 100)
                                            : 0}%
                                    </p>
                                </div>
                            </div>
                            <div className="h-[8px] w-full bg-[rgba(255,255,255,0.08)] rounded-[16px]">
                                <div
                                    className="h-full bg-[#f3ce28] rounded-[16px] transition-all duration-500"
                                    style={{
                                        width: `${liveDepositTarget > 0 ? Math.min((liveDepositCurrent / liveDepositTarget) * 100, 100) : 0}%`,
                                    }}
                                />
                            </div>
                        </div>

                        {/* ─── Combined Wallet + Deposit Card ─── */}
                        <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px]">
                            {/* Wallet Balance section */}
                            <div className="flex items-center justify-between overflow-clip pt-[16px] pb-[20px] px-[16px] rounded-[16px]">
                                <div className="flex gap-[8px] items-center">
                                    <Icon name="wallet" size={16} />
                                    <p className="font-semibold text-[14px] text-white">
                                        Wallet Balance
                                    </p>
                                </div>
                                <div className="flex items-center gap-[8px]">
                                    <p className="font-bold text-[16px] text-white tabular-nums">
                                        {publicKey
                                            ? balanceLoading
                                                ? "Loading..."
                                                : `${phantomBalance.toFixed(3)} SOL`
                                            : "Not connected"}
                                    </p>
                                    {/* {!publicKey && (
                                        <button
                                            onClick={connectWallet}
                                            className="px-[12px] py-[6px] rounded-[30px] bg-white/5 text-[11px] font-semibold text-white hover:bg-white/10 transition-colors"
                                        >
                                            Connect
                                        </button>
                                    )} */}
                                </div>
                            </div>

                            {/* Deposit Input section */}
                            <div className="bg-[rgba(255,255,255,0.02)] border-t-[0.5px] border-[rgba(255,255,255,0.2)] flex flex-col gap-[clamp(16px,2.1dvh,20px)] overflow-clip pt-[20px] pb-[16px] px-[16px] rounded-bl-[16px] rounded-br-[16px]">
                                <p className="font-semibold text-[clamp(18px,2.1dvh,20px)] text-white">
                                    Enter Deposit Amount
                                </p>

                                {/* Amount input box */}
                                <div className="bg-[#0a0a0a] border-[0.5px] border-[rgba(255,255,255,0.2)] flex items-center justify-between px-[24px] py-[16px] rounded-[16px]">
                                    <input
                                        className="w-full bg-transparent border-none text-[clamp(30px,3.9dvh,36px)] leading-none font-extrabold text-white p-0 focus:outline-none focus:ring-0 placeholder:text-white/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0.00"
                                        type="number"
                                        min={effectiveMin}
                                        max={effectiveMax}
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => {
                                            rawAmountRef.current = null;
                                            const val = parseFloat(e.target.value);
                                            if (!isNaN(val) && val > effectiveMax) {
                                                rawAmountRef.current = effectiveMax;
                                                setAmount(String(effectiveMaxDisplay));
                                            } else {
                                                setAmount(e.target.value);
                                            }
                                        }}
                                        disabled={effectiveMax <= 0}
                                    />
                                    <span className="font-semibold text-[13px] text-white shrink-0 ml-[8px]">
                                        SOL
                                    </span>
                                </div>

                                {/* Quick amount pills */}
                                <div className="flex gap-[6px] h-[33px]">
                                    {QUICK_AMOUNTS.filter((val) => val <= effectiveMax).map((val) => (
                                        <button
                                            key={val}
                                            className="bg-[rgba(255,255,255,0.05)] flex-1 flex items-center justify-center rounded-[30px] px-[6px] text-[clamp(11px,1.2dvh,13px)] font-semibold text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors whitespace-nowrap"
                                            onClick={() => { rawAmountRef.current = null; setAmount(String(val)); }}
                                        >
                                            {val} SOL
                                        </button>
                                    ))}
                                    <button
                                        className="bg-[rgba(243,206,40,0.05)] border border-[#f3ce28] flex-1 flex items-center justify-center rounded-[30px] px-[6px] text-[clamp(11px,1.2dvh,13px)] font-semibold text-[#f3ce28] hover:bg-[rgba(243,206,40,0.1)] transition-colors"
                                        onClick={() => { rawAmountRef.current = effectiveMax; setAmount(String(effectiveMaxDisplay)); }}
                                        disabled={effectiveMax <= 0}
                                    >
                                        Max
                                    </button>
                                </div>

                                {/* Divider */}
                                <div className="h-px w-full bg-[rgba(255,255,255,0.1)]" />

                                {/* Min/Max hint */}
                                <div className="flex gap-[6px] items-center">
                                    <span className="font-semibold text-[10px] text-[rgba(255,255,255,0.3)]">
                                        MIN: {parseFloat(effectiveMin.toFixed(4))} SOL
                                    </span>
                                    <div className="w-px self-stretch bg-[#333]" />
                                    <span className="font-semibold text-[10px] text-[rgba(255,255,255,0.3)]">
                                        MAX: {effectiveMaxDisplay} SOL
                                    </span>
                                    {/* {effectiveMax < cabal.maxDeposit && (
                                        <>
                                            <div className="w-px self-stretch bg-[#333]" />
                                            <span className="font-semibold text-[10px] text-[rgba(255,255,255,0.3)]">
                                                (Capped by remaining capacity)
                                            </span>
                                        </>
                                    )} */}
                                </div>
                            </div>
                        </div>

                        {/* ─── Preview Card ─── */}
                        {numAmount > 0 && (
                            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] flex flex-col gap-[20px] overflow-clip p-[16px] rounded-[12px]">
                                <div className="flex items-end justify-between">
                                    <p className="font-medium leading-[1.15] text-[14px] text-[rgba(255,255,255,0.4)] tracking-[-0.28px]">
                                        Est. % of Supply
                                    </p>
                                    <p className="font-bold text-[16px] text-white">
                                        {supplyPct}%
                                    </p>
                                </div>
                                <div className="h-px w-full bg-[rgba(44,44,44,0.6)]" />
                                <div className="flex items-end justify-between">
                                    <p className="font-medium leading-[1.15] text-[14px] text-[rgba(255,255,255,0.4)] tracking-[-0.28px]">
                                        Est. Allocation
                                    </p>
                                    <p className="font-bold text-[16px] text-[#f3ce28] truncate">
                                        {estAllocation} {cabal.ticker.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Insufficient balance warning */}
                        {numAmount > 0 && publicKey && !hasEnoughSOL && (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-[12px] p-[16px] flex items-center gap-[8px]">
                                <p className="text-amber-400/80 text-[11px] font-semibold leading-tight">
                                    Insufficient SOL in wallet. Available: {phantomBalance.toFixed(4)} SOL.
                                </p>
                            </div>
                        )}

                        {/* Error display */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-[12px] p-[16px]">
                                <p className="text-red-400 text-[12px] font-semibold">{error}</p>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* ─── Bottom CTA ─── */}
            {cabal && (
                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 py-[clamp(12px,1.7dvh,20px)] px-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent">
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        loading={submitting}
                        disabled={
                            publicKey
                                ? !isValidAmount || !hasEnoughSOL || submitting
                                : false
                        }
                        onClick={publicKey ? handleDeposit : connectWallet}
                        className="max-w-[400px] mx-auto text-[20px]"
                    >
                        {!publicKey
                            ? "Connect Wallet"
                            : effectiveMax <= 0
                                ? (depositsFull && rawRemaining > 0 ? "Launching Soon" : "Codev Deposit Full")
                                : !isValidAmount
                                    ? `Enter ${parseFloat(effectiveMin.toFixed(4))}–${effectiveMaxDisplay} SOL`
                                    : !hasEnoughSOL
                                        ? "Insufficient Balance"
                                        : stepLabel}
                    </Button>
                </div>
            )}

            <MobileWalletSheet
                open={mobileSheetOpen}
                onClose={() => setMobileSheetOpen(false)}
            />
        </div>
    );
}
